import React from 'react';
import { formatDate, formatDateWords } from './tenantClock';

// Shared pill visual language — originated in PriceTable.js's StatusPill,
// mirrored once already by ProductPricesPage.js's LockedPill. Pulled out
// here so every screen wanting a status/locked indicator draws from the
// same shape instead of each re-copying its own PILL_STYLE.
export const PILL_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 12,
  fontWeight: 600,
  padding: '3px 9px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
};

export function Pill({ color, background, title, style, children, onClick }) {
  return (
    <span
      style={{ ...PILL_STYLE, color, background, ...(onClick ? { cursor: 'pointer' } : null), ...style }}
      title={title}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } } : undefined}
    >
      {children}
    </span>
  );
}

// Same red as ProductPricesPage.js's LockedPill — a record that can't be
// renamed/deactivated because reports or other data depend on it staying
// put, whatever the specific reason on this particular screen.
export function LockedPill({ title }) {
  return <Pill color="#b91c1c" background="#fef2f2" title={title}>Locked</Pill>;
}

// Lightens a "#rrggbb" accent colour toward white for use as a Pill
// background, keeping the accent itself as the text colour — the same
// dark-text/pale-bg pairing the original hardcoded badge colors used, just
// derived from one tenant-configurable hex instead of two fixed ones.
export function tint(hex, amount = 0.88) {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return hex;
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const mix = (c) => Math.round(c + (255 - c) * amount);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

// Month + 2-digit year only, no day — the pill label needs "roughly when";
// the exact date lives in the hover (formatExactDate) and on the Product.
function formatAvailabilityDate(dateStr) {
  if (!dateStr) return '';
  const month = formatDateWords(dateStr, { month: 'short' });
  if (!month) return '';
  return `${month} '${dateStr.slice(2, 4)}`;
}

// '01/12/2026' (in the company Date Format) — the Products list's staff
// view shows the full date.
function formatFullDate(dateStr) {
  return formatDate(dateStr);
}

function formatExactDate(dateStr) {
  return formatDateWords(dateStr);
}

// Evolving availability badge (see availabilityResolver.js) — the text
// changes through the lifecycle instead of separate Coming Soon/Backorder/
// New components. `color` is the tenant's configured accent for whichever
// stage `badge` is currently in (Sales Settings); falls back to the original
// MVP palette if a caller doesn't have settings loaded.
//
// Same pills and hover on every screen (Products list, Pricelist screen and
// print reports, Quote/Order lines, and the customer portal to come):
//   - From Dec '26      not orderable yet, arriving on that date
//   - Pre-order + date  orderable ahead of launch (date inside the lead
//                       window, or the Pre-order override)
//   - Backorder + date  orderable ahead of stock (Backorder override on a
//                       normally stocked SKU that's out)
//                       Either way the order ships when stock arrives, and
//                       the date ("ETA Dec '26" / "ETA TBA") is a second,
//                       neutral pill so the two can wrap in a narrow column.
//                       The word itself tells a customer on a printed/emailed
//                       PDF (no hover) that it ships later, not now.
//   - New               launched recently (the Extended Pricelist's stacked
//                       variant adds a solid "Order now" pill under it)
// The hover on any pill carries the full sentence and exact date.
const BADGE_DEFAULT_COLOR = {
  available_soon: '#8a5a00',
  backorder_now: '#5a3e00',
  preorder_now: '#5a3e00',
  new: '#1b5e20',
};

// ETA pill next to "Pre-order"/"Backorder": neutral grey so it reads as timing info,
// not a second status.
const ETA_COLOR = '#5f6b7a';
const ETA_BACKGROUND = '#eceff3';

const ORDER_NOW_BADGES = ['backorder_now', 'preorder_now'];

// True when the badge renders as two stacked pills (Pre-order/Backorder over its date)
// rather than one — callers laying out around AvailabilityCornerBadge need
// to know how far down the corner it reaches.
export function isStackedAvailabilityBadge(badge) {
  return ORDER_NOW_BADGES.includes(badge);
}

function badgePills(badge, date, accent) {
  const shortDate = formatAvailabilityDate(date);
  const exactDate = formatExactDate(date);
  if (badge === 'available_soon') {
    return [{ key: 'main', text: `From ${shortDate}`, color: accent, background: tint(accent), hover: `Available from ${exactDate}` }];
  }
  if (ORDER_NOW_BADGES.includes(badge)) {
    const label = badge === 'preorder_now' ? 'Pre-order' : 'Backorder';
    const hover = exactDate
      ? `${label} – ships when stock arrives, expected ${exactDate}`
      : `${label} – ships when stock arrives, ETA to be confirmed`;
    return [
      { key: 'main', text: label, color: accent, background: tint(accent), hover },
      { key: 'eta', text: `ETA ${shortDate || 'TBA'}`, color: ETA_COLOR, background: ETA_BACKGROUND, hover },
    ];
  }
  const hover = exactDate ? `New – available since ${exactDate}` : 'New';
  return [{ key: 'main', text: 'New', color: accent, background: tint(accent), hover }];
}

// `showDate` is for staff screens: with no stage active, the Availability
// Date shows as plain muted text instead of nothing. Pricelists, printed
// reports and the portal leave it off — a customer never sees an old
// launch date (ui-architecture.md "Availability").
export function AvailabilityPill({ badge, date, color, showDate = false }) {
  if (badge && BADGE_DEFAULT_COLOR[badge]) {
    const pills = badgePills(badge, date, color || BADGE_DEFAULT_COLOR[badge]);
    return (
      <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
        {pills.map((p) => (
          <Pill key={p.key} color={p.color} background={p.background} title={p.hover}>{p.text}</Pill>
        ))}
      </span>
    );
  }
  if (showDate && date) {
    return (
      <span title={`Availability Date: ${formatExactDate(date)}`} style={{ fontSize: 13, color: '#5f6b7a', whiteSpace: 'nowrap' }}>
        {formatFullDate(date)}
      </span>
    );
  }
  return null;
}

const OVERRIDE_LABEL = {
  hidden: 'Hidden',
  backorder: 'Backorder',
  preorder: 'Pre-order',
  available: 'Available',
};

// Staff view of a Product's availability settings exactly as entered — the
// date, the Override, and any suppressed badge stage — for the Products list
// (data entry / verification). Every other screen uses AvailabilityPill
// instead (with showDate on staff screens).
export function AvailabilityAsEntered({ date, override, hidePreBadge, hideNewBadge }) {
  const fullDate = formatFullDate(date);
  const overrideLabel = OVERRIDE_LABEL[override] || '';
  const mainParts = [fullDate, overrideLabel].filter(Boolean);
  const main = !fullDate && overrideLabel ? `${overrideLabel} (no date)` : mainParts.join(' · ');
  const hidden = [hidePreBadge && 'Pre-launch badge hidden', hideNewBadge && 'New badge hidden'].filter(Boolean);
  if (!main && hidden.length === 0) return null;
  const hover = [
    date && `Availability Date: ${formatExactDate(date)}`,
    overrideLabel && `Override: ${overrideLabel}`,
    ...hidden,
  ].filter(Boolean).join('\n');
  return (
    <span title={hover} style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.3 }}>
      {main && <span style={{ color: '#3a4556' }}>{main}</span>}
      {hidden.length > 0 && <span style={{ fontSize: 12, color: '#8a94a3' }}>{hidden.join(' · ')}</span>}
    </span>
  );
}

// Print-report variant for a narrow column with height to spare (Extended
// Pricelist's SKU/photo cell, under the photo): in normal flow, centred, one
// pill per line at the corner badge's smaller size. Same pills and hover as
// AvailabilityPill.
export function AvailabilityStackedBadge({ badge, date, color }) {
  if (!badge || !BADGE_DEFAULT_COLOR[badge]) return null;
  const accent = color || BADGE_DEFAULT_COLOR[badge];
  const pills = badgePills(badge, date, accent);
  // "New" on its own is a status with no next step, unlike Pre-order/Backorder
  // whose second pill carries the ETA. A solid accent-filled "Order now" gives
  // a printed/emailed price list (no hover, no click) a call to action.
  if (badge === 'new') {
    pills.push({ key: 'cta', text: 'Order now', color: '#fff', background: accent, hover: 'Available to order now' });
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, marginTop: 4 }}>
      {pills.map((p) => (
        <Pill
          key={p.key}
          color={p.color}
          background={p.background}
          title={p.hover}
          style={{ fontSize: 10, padding: '2px 6px' }}
        >
          {p.text}
        </Pill>
      ))}
    </div>
  );
}

// Print-report variant that can never grow the card it sits in: `position:
// absolute` takes it out of document flow entirely, so it doesn't matter
// how long a product's name is or how few price-break rows it has — every
// card/cell stays exactly the size its own price rows dictate, same as a
// product with no badge at all. The caller's positioned ancestor must be
// `position: relative` and a fixed size (a card header bar, or a wrapper
// around the product thumbnail) for this to land in its corner. Same pills
// and hover as AvailabilityPill, smaller, and stacked vertically when there
// are two (Pre-order/Backorder over its date).
export function AvailabilityCornerBadge({ badge, date, color, corner = { top: 4, right: 6 } }) {
  if (!badge || !BADGE_DEFAULT_COLOR[badge]) return null;
  const pills = badgePills(badge, date, color || BADGE_DEFAULT_COLOR[badge]);
  return (
    <span
      style={{
        position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 2,
        ...corner,
      }}
    >
      {pills.map((p) => (
        <Pill
          key={p.key}
          color={p.color}
          background={p.background}
          title={p.hover}
          style={{ fontSize: 10, padding: '2px 6px' }}
        >
          {p.text}
        </Pill>
      ))}
    </span>
  );
}

// Fixed 8-hue palette + string hash — for categorical columns (Product
// Type, Relationship, Lifecycle Stage, etc.) that have no tenant-configured
// color of their own, unlike Availability's Sales-Setup-driven accent
// above. Same key always resolves to the same color within a session and
// across screens, without a schema change or a per-tenant color picker.
const CATEGORY_PALETTE = ['#1976d2', '#7b1fa2', '#c2185b', '#00838f', '#2e7d32', '#ef6c00', '#5d4037', '#455a64'];

export function categoricalColor(key) {
  const str = String(key ?? '');
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return CATEGORY_PALETTE[hash % CATEGORY_PALETTE.length];
}

// Generic categorical pill — `value` is both the displayed text and the
// hash key by default, so the same value always lands on the same color
// wherever it appears (e.g. "Wholesale" colors the same on Products and
// Leads). Pass `color` to pin a specific value to a deliberate accent
// instead of whatever the hash happens to land on — e.g. a lifecycle's
// terminal states, where an arbitrary hash collision (Withdrawn landing on
// the same green as an Active/success state) would read as the wrong
// signal (see QuotePage.js's QUOTE_STATUS_COLORS).
export function CategoryPill({ value, title, color }) {
  if (!value) return null;
  const accent = color || categoricalColor(value);
  return <Pill color={accent} background={tint(accent)} title={title}>{value}</Pill>;
}

// Same green/grey pairing as PriceTable.js's own StatusPill (its
// non-expiring case) — the standard Active/Inactive badge for every
// list screen's Status column, so that common case doesn't need its own
// bespoke pill component per screen.
export function ActiveStatusPill({ active, activeLabel = 'Active', inactiveLabel = 'Inactive' }) {
  return active
    ? <Pill color="#1a7f4b" background="rgba(26, 127, 75, 0.12)">{activeLabel}</Pill>
    : <Pill color="#5a6b85" background="#dde3ea">{inactiveLabel}</Pill>;
}

// A record locked because it's the system's one reserved instance of
// something (e.g. the Retail Customer Price Group) — distinct from
// LockedPill's "in use elsewhere" reason, so the two aren't conflated even
// though both end up disabling the same actions.
export function SystemPill({ title = "System Use — required by Optim's core functionality." }) {
  return <Pill color="#3b5a8a" background="#e2e9f4" title={title}>System</Pill>;
}

// Soft, non-blocking flag for a Product+UoM with no Retail Price set yet
// (vis_prices-and-pricelists.md) — never blocks saving, only surfaces the
// gap at data-entry (see docs/product/platform/architecture/ui-architecture.md
// §25 Soft Warning, Not Hard Block). Same amber as PriceTable.js's own
// "Active ⚠ Expired" state — one amber convention across the app, not a
// third invented here.
export function NeedsRetailPricePill({ name = 'Retail', title }) {
  return <Pill color="#b45309" background="#fdf1e0" title={title || `No ${name} Price set for this Product/UoM yet`}>Needs {name} Price</Pill>;
}

// Same soft-warning convention as NeedsRetailPricePill above, for a
// Sales Order/Quote line whose Product+UoM has no Weight set (see
// ProductWeightsPage.js) — flags that a printed/on-screen Total Weight is
// incomplete without blocking Save.
// A Sales Order Product or Customer not linked to an active QuickBooks
// record (issue #268): red, since it blocks Accept. `reason` is
// quickbooksAcceptGate.js's: 'unlinked' | 'inactive' | 'missing'; `noun`
// is 'Product' or 'Customer'.
function quickbooksUnlinkedTitle(noun, reason) {
  if (reason === 'inactive') return `This ${noun}’s QuickBooks record is inactive, so the order can’t be Accepted`;
  if (reason === 'missing') return `This ${noun}’s QuickBooks record is missing, so the order can’t be Accepted`;
  return `This ${noun} isn’t linked to QuickBooks, so the order can’t be Accepted`;
}
// `linkable`: the caller wraps it in a link to its QuickBooks tab — adds the
// "→" and a hover underline (same cue as an "… →" guide link) so it reads
// as clickable, and says where it goes in the hover.
export function NotLinkedToQuickBooksPill({ reason = 'unlinked', noun = 'Product', linkable = false }) {
  const title = quickbooksUnlinkedTitle(noun, reason);
  if (!linkable) return <Pill color="#b91c1c" background="#fef2f2" title={title}>Not linked to QuickBooks</Pill>;
  return (
    <span className="qb-unlinked-pill">
      <Pill color="#b91c1c" background="#fef2f2" title={`${title}. Click to link it in QuickBooks > ${noun}s.`} style={{ border: '1px solid #fecaca', cursor: 'pointer' }}>
        Not linked to QuickBooks →
      </Pill>
      <style>{'.qb-unlinked-pill:hover > span { background: #fee2e2 !important; text-decoration: underline; }'}</style>
    </span>
  );
}

export function NeedsWeightPill({ title }) {
  return <Pill color="#b45309" background="#fdf1e0" title={title || 'One or more lines are missing a Weight'}>Missing Weight</Pill>;
}

// Same soft-warning convention, for a Quote line priced at Retail only
// because the Customer holds no Customer Price Group for it — no
// assignment, no Tier default (vis_relationship_lifecycle.md §6: Retail is
// chosen, never fallen back to). Never blocks Save.
export function RetailFallbackPill({ name = 'Retail', title }) {
  return <Pill color="#b45309" background="#fdf1e0" title={title || `Priced at ${name} — this Customer has no Customer Price Group for this Product`}>{name} fallback</Pill>;
}

// Four-state pill for a Google-Places-backed field (Opening Hours,
// Address) — always shown, same "never blank" pattern as ActiveStatusPill
// above. `staleLabel` is the amber aged-out state's text ("Hours
// Stale"/"Address Stale"); `untried` is a separate amber state for a
// record that's never been checked at all, which reads as more overdue
// than "stale" (a check that just aged out) — same "never checked isn't a
// pass" reasoning as openingHoursStaleness.js/addressStaleness.js, just
// given its own label rather than collapsing into the aged-out wording.
// Every state is clickable, green Retrieved included: `stale`/`untried`
// here are a timestamp-based guess ("has this been edited recently"), not
// proof Google actually confirmed the current value — a manual edit (e.g.
// correcting a Country) marks a record "fresh" without Google ever having
// re-checked it, so locking the retry out on green would trap exactly the
// case that most needs a re-check.
export function GoogleLookupPill({ stale, noMatch, staleLabel, untried, checking, onClick, style }) {
  if (checking) {
    return <Pill color="#5a6b85" background="#dde3ea" style={style}>Checking…</Pill>;
  }
  if (!stale) {
    return <Pill color="#1a7f4b" background="rgba(26, 127, 75, 0.12)" title="Click to check again on Google Places" style={style} onClick={onClick}>Retrieved</Pill>;
  }
  if (noMatch) {
    return (
      <Pill color="#5a6b85" background="#dde3ea" title="Google has no listing for this business — click to retry" style={style} onClick={onClick}>
        Unlisted
      </Pill>
    );
  }
  if (untried) {
    return (
      <Pill color="#b45309" background="#fdecc8" title="Never looked up — click to look up on Google Places" style={style} onClick={onClick}>
        Untried
      </Pill>
    );
  }
  return (
    <Pill color="#b45309" background="#fdecc8" title={`${staleLabel} — click to look up on Google Places`} style={style} onClick={onClick}>
      {staleLabel}
    </Pill>
  );
}
